import * as React from 'react'
import classNames from 'classnames'

import type * as Flows from '../../../shared/domain/flows/types.no-deps'
import { elementDomId } from './element'
import type { CommonDeps } from '../base/CommonDeps'
import type { TimelineDetails } from './TimelineDetails'
import { type LoopPathIds, toLoopPath } from '../../../shared/domain/flows/LoopPath.no-deps'
import AnswerTimeline from '../../../shared/webcomponents/react-wrappers/AnswerTimeline'

interface Props {
    deps: CommonDeps
    elementId: Flows.ElementId
    loopPathIds: LoopPathIds
    children: React.ReactNode
    timelineDetails?: TimelineDetails
    validationMessages: string[]
    inline?: boolean
}

/**
 * A container for the contents of every executable Flow element. It handles:
 * - Focusing on a suitable child of the element when any part of the element is
 *   clicked
 * - Displaying the `AnswerTimeline` for the given element
 * - Sharing some styles between elements
 */
export function ExecutionElement(props: Props) {
    const invalid = props.validationMessages.length > 0
    const inline = props.inline || false
    const editing = props.timelineDetails !== null

    return (
        <div
            className={classNames('AppCore__flow__ExecutionElement', { invalid, inline, editing })}
            id={elementDomId(props.elementId, toLoopPath(props.loopPathIds))}
            key={props.elementId}
            onClick={(event) => {
                const target = event.currentTarget
                const input = target.querySelector<HTMLElement>('.focusable')
                if (input == null) return
                input.focus()
            }}
        >
            {props.children}
            {props.timelineDetails && (
                <div className="AppCore__flow__ExecutionElement__answer-timeline">
                    <AnswerTimeline
                        answers={props.timelineDetails.elementAnswers}
                        choices-lookup={props.timelineDetails.choicesLookup}
                        guide-names-lookup={null}
                        language={props.deps.language}
                        locale={props.deps.locale}
                        users={props.timelineDetails.userProfiles}
                    ></AnswerTimeline>
                </div>
            )}
        </div>
    )
}
